/**
 * MailboxPage — 收件箱/邮件查询（左中右布局）
 * 左栏：收件域选择 | 中栏：该域下的邮箱账号 | 右栏：按所选邮箱查询的收件箱（保留原邮件表格与详情）
 */
import { useMemo, useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  makeStyles,
  tokens,
  Text,
  Input,
  Button,
  Card,
  Field,
  InfoLabel,
  Spinner,
  Switch,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogContent,
  DialogTitle,
  Avatar,
  Badge,
  Tooltip,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
} from '@fluentui/react-components'
import {
  SearchRegular,
  DismissRegular,
  ArrowSyncRegular,
  ArrowDownloadRegular,
  GlobeRegular,
  MailRegular,
  PersonMailRegular,
  Star24Regular,
  Star24Filled,
  Attach24Regular,
  Print24Regular,
  MoreHorizontal24Regular,
  ArrowReplyAll24Regular,
  ArrowForward24Regular,
  ChevronDown24Regular,
  ChevronUp24Regular,
} from '@fluentui/react-icons'
import { mailboxApi } from '@/api/mailbox'
import { domainsApi } from '@/api/domains'
import { usersApi } from '@/api/users'
import type { MailboxDetail, MailboxItem, Domain } from '@/api/types'
import { PagedGrid, type PagedColumn } from '@/components/PagedGrid'
import ComposeModal from './ComposeModal'
import { formatBytes, formatDate } from '@/lib/format'
import { toast } from '@/lib/toast'

const MAIL_PAGE = 10
const LEFT_PAGE = 10
const MID_PAGE = 20

const useStyles = makeStyles({
  root: { padding: '8px 12px', height: '100%', boxSizing: 'border-box' },
  inner: { height: '100%', display: 'flex', flexDirection: 'column' },
  split: {
    display: 'flex',
    gap: '16px',
    alignItems: 'stretch',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    padding: '2px 0 2px 2px',
  },
  // 左栏 / 中栏：统一的选择列表面板
  selCard: {
    flex: '0 0 260px',
    minWidth: '220px',
    display: 'flex',
    flexDirection: 'column',
    padding: '12px',
    height: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  midCard: { flex: '0 0 300px', minWidth: '260px' },
  selHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    paddingBottom: '10px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    marginBottom: '8px',
  },
  selTitle: { fontWeight: tokens.fontWeightSemibold, flex: 1 },
  filterRow: { display: 'flex', gap: '8px', marginBottom: '8px', flexShrink: 0 },
  listWrap: { flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' },
  list: { display: 'flex', flexDirection: 'column', gap: '2px' },
  item: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    padding: '8px 10px',
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    border: '1px solid transparent',
    ':hover': { background: tokens.colorNeutralBackground1Hover },
  },
  itemActive: {
    background: tokens.colorBrandBackground2,
    border: `1px solid ${tokens.colorBrandStroke2}`,
    ':hover': { background: tokens.colorBrandBackground2 },
  },
  itemBody: { display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0, flex: 1 },
  itemTop: { display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0, flexWrap: 'wrap' },
  itemName: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
  },
  itemSub: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    minWidth: 0,
  },
  emptyList: { padding: '24px 8px', textAlign: 'center', color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  pager: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexShrink: 0,
    paddingTop: '8px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    marginTop: '8px',
  },
  pageInfo: { fontSize: tokens.fontSizeBase100, color: tokens.colorNeutralForeground3, flex: 1 },
  // 右栏：收件箱
  right: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' },
  rightRoot: { display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 },
  rightHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    paddingBottom: '10px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    marginBottom: '10px',
    flexShrink: 0,
  },
  rightTitle: { fontWeight: tokens.fontWeightSemibold },
  queryRow: { display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0, marginBottom: '10px' },
  queryField: { flex: 1, minWidth: '160px' },
  // 右栏内容包裹：四周留出内边距，避免卡片阴影/边框被裁切
  tableWrap: { flex: 1, minHeight: 0, minWidth: 0, overflowY: 'auto', padding: '0 2px 10px 2px' },
  tableCard: { padding: '16px', boxSizing: 'border-box' },
  placeholder: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    color: tokens.colorNeutralForeground3,
  },
  dialogSurface: { width: '65vw', minWidth: '600px', maxWidth: '1000px' },
  bodyBox: {
    background: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    padding: '12px',
    marginTop: '8px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontSize: tokens.fontSizeBase300,
    maxHeight: '320px',
    overflow: 'auto',
  },
  htmlFrame: {
    width: '100%',
    height: '320px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    background: '#fff',
    marginTop: '8px',
  },
  attRow: { display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0' },
  attName: { flex: 1, fontSize: '13px', color: tokens.colorNeutralForeground2, wordBreak: 'break-all' },
  // ── 详情对话框（MailClient 美化） ──
  detailHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '4px 4px 12px 4px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    marginBottom: '16px',
  },
  detailSubject: {
    flex: 1,
    minWidth: 0,
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  detailActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    flexShrink: 0,
  },
  // 顶部发件人卡片
  mailHero: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '14px',
    paddingBottom: '16px',
  },
  avatarBox: {
    flexShrink: 0,
    paddingTop: '2px',
  },
  heroInfo: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  heroTopRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
  },
  heroFrom: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  heroEmail: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    wordBreak: 'break-all',
  },
  heroEmailStrong: { color: tokens.colorNeutralForeground2, fontWeight: tokens.fontWeightSemibold, marginRight: '4px' },
  heroHideToggle: {
    alignSelf: 'flex-end',
    flexShrink: 0,
    background: 'transparent',
    border: 'none',
    color: tokens.colorBrandForeground1,
    cursor: 'pointer',
    padding: '2px 4px',
    fontSize: tokens.fontSizeBase200,
    ':hover': { textDecoration: 'underline' },
  },
  divider: { height: '1px', background: tokens.colorNeutralStroke2, margin: '4px 0 16px 0' },
  // 邮件正文
  bodyContainer: { display: 'flex', flexDirection: 'column', gap: '12px' },
  bodyFrame: {
    width: '100%',
    minHeight: '280px',
    maxHeight: '420px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    background: tokens.colorNeutralBackground1,
  },
  bodyText: {
    background: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    padding: '14px 16px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontSize: tokens.fontSizeBase300,
    lineHeight: '1.6',
    maxHeight: '380px',
    overflow: 'auto',
  },
  // 附件
  attSection: { display: 'flex', flexDirection: 'column', gap: '8px' },
  attItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 12px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    background: tokens.colorNeutralBackground1,
    ':hover': { background: tokens.colorNeutralBackground1Hover },
  },
  // 底部快捷回复
  replyBox: {
    marginTop: '18px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    background: tokens.colorNeutralBackground2,
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  replyLabel: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground2 },
  replyInput: { minHeight: '72px', fontFamily: 'inherit' },
  replyActions: { display: 'flex', justifyContent: 'flex-end', gap: '8px' },
  metaLabel: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    marginRight: '6px',
  },
})

/** 下载 base64 附件（data URL 触发下载） */
function downloadAttachment(att: { filename: string; content_type: string; data: string }) {
  const href = `data:${att.content_type || 'application/octet-stream'};base64,${att.data}`
  const a = document.createElement('a')
  a.href = href
  a.download = att.filename || 'attachment'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

/** 逐个下载全部附件（错开触发，避免浏览器拦截多次下载） */
function downloadAllAttachments(atts: { filename: string; content_type: string; data: string }[]) {
  atts.forEach((a, i) => setTimeout(() => downloadAttachment(a), i * 300))
}

/** 从 "姓名 <email@x>" 或纯 email 中抽离可读的发件人显示名（avatar 用） */
function fromName(from: string | null): string {
  if (!from) return '?'
  // 优先取 "<" 前的部分
  const lt = from.indexOf('<')
  if (lt > 0) {
    const name = from.slice(0, lt).trim().replace(/^["']|["']$/g, '')
    if (name) return name
  }
  // 否则去掉 @ 及之后，用本地部分
  const at = from.indexOf('@')
  if (at > 0) return from.slice(0, at)
  return from
}

/** 构造"回复"邮件正文：引用原文 + 发件人/时间前缀 */
function buildReplyBody(detail: MailboxDetail): string {
  const quoted = (detail.body_text || '')
    .split('\n')
    .map((l) => `> ${l}`)
    .join('\n')
  return `\n\n在 ${formatDate(detail.created_at)}，${detail.from ?? '发件人'} 写道：\n${quoted}`
}

/** 构造"转发"邮件正文：带上原始头 + 原文 */
function buildForwardBody(detail: MailboxDetail): string {
  return `\n\n---------- 转发邮件 ----------\n发件人: ${detail.from ?? '（未知）'}\n收件人: ${detail.recipient}\n时间: ${formatDate(detail.created_at)}\n主题: ${detail.subject ?? '（无主题）'}\n\n${detail.body_text || ''}`
}

export default function MailboxPage() {
  const styles = useStyles()

  // ── 左栏：收件域 ──
  const { data: domains } = useQuery({ queryKey: ['domains'], queryFn: () => domainsApi.list() })
  const [domainSearch, setDomainSearch] = useState('')
  const [domainPage, setDomainPage] = useState(1)
  const [selectedDomainId, setSelectedDomainId] = useState<number | null>(null)

  const filteredDomains = useMemo(() => {
    const s = domainSearch.trim().toLowerCase()
    const list = domains ?? []
    return s ? list.filter((d) => d.name.toLowerCase().includes(s)) : list
  }, [domains, domainSearch])
  const domainPages = Math.max(1, Math.ceil(filteredDomains.length / LEFT_PAGE))
  const safeDomainPage = Math.min(domainPage, domainPages)
  const domainItems = filteredDomains.slice((safeDomainPage - 1) * LEFT_PAGE, safeDomainPage * LEFT_PAGE)
  const selectedDomain: Domain | undefined = domains?.find((d) => d.id === selectedDomainId)

  // ── 中栏：该域下的邮箱 ──
  const { data: users } = useQuery({ queryKey: ['users-all'], queryFn: () => usersApi.list(1, 500) })
  const [emailSearch, setEmailSearch] = useState('')
  const [midPage, setMidPage] = useState(1)
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null)

  const domainUsers = useMemo(() => {
    const s = emailSearch.trim().toLowerCase()
    const list = (users ?? []).filter((u) => u.domain === selectedDomain?.name)
    return s
      ? list.filter((u) => u.email.toLowerCase().includes(s) || (u.display_name ?? '').toLowerCase().includes(s))
      : list
  }, [users, selectedDomain, emailSearch])
  const midPages = Math.max(1, Math.ceil(domainUsers.length / MID_PAGE))
  const safeMidPage = Math.min(midPage, midPages)
  const midItems = domainUsers.slice((safeMidPage - 1) * MID_PAGE, safeMidPage * MID_PAGE)

  // 切换域名 → 清空已选邮箱
  useEffect(() => {
    setSelectedEmail(null)
    setMidPage(1)
    setEmailSearch('')
  }, [selectedDomainId])

  // ── 右栏：按所选邮箱的收件箱 ──
  const [draftQ, setDraftQ] = useState('')
  const [q, setQ] = useState('')
  const [page, setPage] = useState(1)
  const [reloadKey, setReloadKey] = useState(0)
  const [detail, setDetail] = useState<MailboxDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [selectionMode, setSelectionMode] = useState<'single' | 'multiselect'>('single')
  // 详情对话框：星标/隐藏信息/写邮件
  const [starred, setStarred] = useState(false)
  const [showMeta, setShowMeta] = useState(true)
  const [composeOpen, setComposeOpen] = useState(false)
  const [composeInit, setComposeInit] = useState<{
    from: string
    to: string
    subject: string
    body: string
    title: string
  }>({ from: '', to: '', subject: '', body: '', title: '写邮件' })

  const { data: result, isLoading } = useQuery({
    queryKey: ['mailbox', selectedEmail, q, page, reloadKey],
    queryFn: () =>
      mailboxApi.list({
        email: selectedEmail ?? undefined,
        q: q || undefined,
        page,
        page_size: MAIL_PAGE,
      }),
    enabled: !!selectedEmail,
    placeholderData: (prev) => prev,
  })

  // 切换邮箱 → 回到第一页、关闭详情、重置对话框状态
  useEffect(() => {
    setPage(1)
    setDetail(null)
    setDetailLoading(false)
    setStarred(false)
    setShowMeta(true)
    setComposeOpen(false)
  }, [selectedEmail])

  const items = result?.items ?? []
  const total = result?.total ?? 0

  const applyQuery = () => {
    setQ(draftQ.trim())
    setPage(1)
  }
  const resetQuery = () => {
    setDraftQ('')
    setQ('')
    setPage(1)
  }

  const deleteMessages = async (rows: MailboxItem[]) => {
    const ids = rows.map((r) => r.id)
    try {
      const res = await mailboxApi.remove(ids)
      toast.success('删除成功', `已删除 ${res.deleted} 封邮件`)
      setDetail(null)
      setReloadKey((k) => k + 1)
    } catch (e) {
      const msg = (e as { message?: string })?.message || '删除失败'
      toast.error('删除失败', msg)
      throw e
    }
  }

  const openDetail = (item: MailboxItem) => {
    setDetail(null)
    setDetailLoading(true)
    mailboxApi
      .get(item.id)
      .then(setDetail)
      .finally(() => setDetailLoading(false))
  }

  const columns: PagedColumn<MailboxItem>[] = [
    {
      key: 'subject',
      header: '主题',
      headerTooltip: '邮件主题，无主题时显示收件人',
      width: '260px',
      render: (r) => <Text weight="semibold">{r.subject || r.recipient}</Text>,
    },
    {
      key: 'from',
      header: '发件人',
      width: '200px',
      render: (r) => r.from ?? '-',
    },
    {
      key: 'recipient',
      header: '收件人',
      width: '200px',
      render: (r) => r.recipient,
    },
    {
      key: 'size',
      header: '大小',
      width: '100px',
      render: (r) => formatBytes(r.size),
    },
    {
      key: 'created_at',
      header: '时间',
      width: '160px',
      render: (r) => formatDate(r.created_at),
    },
  ]

  return (
    <div className={styles.root}>
      <div className={styles.inner}>
        <div className={styles.split}>
          {/* ── 左栏：收件域 ── */}
          <Card className={styles.selCard}>
            <div className={styles.selHeader}>
              <GlobeRegular />
              <Text className={styles.selTitle}>收件域</Text>
            </div>
            <div className={styles.filterRow}>
              <Input
                size="small"
                contentBefore={<SearchRegular />}
                placeholder="搜索域名…"
                value={domainSearch}
                onChange={(_, d) => { setDomainSearch(d.value); setDomainPage(1) }}
              />
            </div>
            <div className={styles.listWrap}>
              {domainItems.length === 0 ? (
                <div className={styles.emptyList}>暂无收件域</div>
              ) : (
                <div className={styles.list}>
                  {domainItems.map((d) => (
                    <div
                      key={d.id}
                      className={`${styles.item} ${selectedDomainId === d.id ? styles.itemActive : ''}`}
                      onClick={() => setSelectedDomainId(d.id)}
                    >
                      <div className={styles.itemBody}>
                        <div className={styles.itemTop}>
                          <span className={styles.itemName}>{d.name}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className={styles.pager}>
              <span className={styles.pageInfo}>共 {filteredDomains.length} 个</span>
              <Button size="small" disabled={safeDomainPage <= 1} onClick={() => setDomainPage((p) => p - 1)}>上一页</Button>
              <Button size="small" disabled={safeDomainPage >= domainPages} onClick={() => setDomainPage((p) => p + 1)}>下一页</Button>
            </div>
          </Card>

          {/* ── 中栏：该域下的邮箱 ── */}
          <Card className={`${styles.selCard} ${styles.midCard}`}>
            <div className={styles.selHeader}>
              <PersonMailRegular />
              <Text className={styles.selTitle}>邮箱账号</Text>
            </div>
            {!selectedDomain ? (
              <div className={styles.emptyList}>请先在左侧选择收件域</div>
            ) : (
              <>
                <div className={styles.filterRow}>
                  <Input
                    size="small"
                    contentBefore={<SearchRegular />}
                    placeholder="搜索邮箱 / 姓名…"
                    value={emailSearch}
                    onChange={(_, d) => { setEmailSearch(d.value); setMidPage(1) }}
                  />
                </div>
                <div className={styles.listWrap}>
                  {midItems.length === 0 ? (
                    <div className={styles.emptyList}>该域暂无邮箱账号</div>
                  ) : (
                    <div className={styles.list}>
                      {midItems.map((u) => (
                        <div
                          key={u.id}
                          className={`${styles.item} ${selectedEmail === u.email ? styles.itemActive : ''}`}
                          onClick={() => setSelectedEmail(u.email)}
                        >
                          <div className={styles.itemBody}>
                            <div className={styles.itemTop}>
                              <span className={styles.itemName}>{u.username}</span>
                            </div>
                            <div className={styles.itemSub}>
                              <MailRegular />
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className={styles.pager}>
                  <span className={styles.pageInfo}>共 {domainUsers.length} 个</span>
                  <Button size="small" disabled={safeMidPage <= 1} onClick={() => setMidPage((p) => p - 1)}>上一页</Button>
                  <Button size="small" disabled={safeMidPage >= midPages} onClick={() => setMidPage((p) => p + 1)}>下一页</Button>
                </div>
              </>
            )}
          </Card>

          {/* ── 右栏：收件箱 ── */}
          <div className={styles.right}>
            {!selectedEmail ? (
              <Card className={styles.placeholder}>
                <SearchRegular style={{ fontSize: 40 }} />
                <Text>请先在左侧选择收件域，再在中间选择邮箱账号</Text>
                <Text size={200}>将展示该邮箱收到的邮件</Text>
              </Card>
            ) : (
              <div className={styles.rightRoot}>
                <div className={styles.rightHeader}>
                  <Badge appearance="tint" color="brand" icon={<MailRegular />}>{selectedEmail}</Badge>
                  <Text className={styles.rightTitle} size={300}>收件箱</Text>
                </div>
                <div className={styles.queryRow}>
                  <Field
                    className={styles.queryField}
                    label={<InfoLabel info="按主题、发件人或收件人地址模糊搜索当前邮箱。">关键词</InfoLabel>}
                  >
                    <Input
                      contentBefore={<SearchRegular />}
                      contentAfter={
                        draftQ ? (
                          <Button appearance="transparent" size="small" icon={<DismissRegular />} aria-label="清空" onClick={() => setDraftQ('')} />
                        ) : undefined
                      }
                      placeholder="搜索主题 / 发件人 / 收件人…"
                      value={draftQ}
                      onChange={(_, d) => setDraftQ(d.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') applyQuery() }}
                    />
                  </Field>
                  <Button appearance="subtle" onClick={resetQuery}>重置</Button>
                  <Button appearance="primary" onClick={applyQuery}>查询</Button>
                  <Button
                    appearance="subtle"
                    icon={<ArrowSyncRegular />}
                    onClick={() => setReloadKey((k) => k + 1)}
                    disabled={isLoading}
                  >
                    {isLoading ? <Spinner size="tiny" /> : '刷新'}
                  </Button>
                </div>
                <div className={styles.tableWrap}>
                  <Card className={styles.tableCard}>
                    <PagedGrid<MailboxItem>
                      rows={items}
                      columns={columns}
                      loading={isLoading}
                      rowKey={(r) => String(r.id)}
                      total={total}
                      page={page}
                      pageSize={MAIL_PAGE}
                      onPageChange={setPage}
                      emptyText="该邮箱暂无邮件，可调整关键词或先写信发送一封"
                      toolbarLeft={
                        <Switch
                          checked={selectionMode === 'multiselect'}
                          onChange={(_, d) => setSelectionMode(d.checked ? 'multiselect' : 'single')}
                          label="多选"
                        />
                      }
                      selectionMode={selectionMode}
                      onRowClick={openDetail}
                      onDelete={deleteMessages}
                    />
                  </Card>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 详情 Dialog — MailClient 风格 */}
      <Dialog
        open={detail !== null || detailLoading}
        onOpenChange={(_, d) => !d.open && (setDetail(null), setDetailLoading(false))}
        modalType="alert"
      >
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogTitle>
              <div className={styles.detailHeader}>
                <Tooltip content="标星" relationship="label">
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={starred ? <Star24Filled /> : <Star24Regular />}
                    aria-label={starred ? '取消标星' : '标星'}
                    onClick={() => setStarred((v) => !v)}
                  />
                </Tooltip>
                <span className={styles.detailSubject}>
                  {detail?.subject || '（无主题）'}
                </span>
                <div className={styles.detailActions}>
                  <Tooltip content="附件" relationship="label">
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<Attach24Regular />}
                      aria-label="附件"
                      onClick={() => document.getElementById('mail-att-anchor')?.scrollIntoView({ behavior: 'smooth' })}
                    />
                  </Tooltip>
                  <Tooltip content="打印" relationship="label">
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<Print24Regular />}
                      aria-label="打印"
                      onClick={() => window.print()}
                    />
                  </Tooltip>
                  <Tooltip content="更多操作" relationship="label">
                    <Menu>
                      <MenuTrigger>
                        <Button appearance="subtle" size="small" icon={<MoreHorizontal24Regular />} aria-label="更多操作" />
                      </MenuTrigger>
                      <MenuPopover>
                        <MenuList>
                          <MenuItem
                            icon={<ArrowDownloadRegular />}
                            onClick={() => detail && downloadAllAttachments(detail.attachments)}
                          >
                            下载全部附件
                          </MenuItem>
                          <MenuItem onClick={() => navigator.clipboard.writeText(detail?.message_id ?? '')}>
                            复制 Message-ID
                          </MenuItem>
                        </MenuList>
                      </MenuPopover>
                    </Menu>
                  </Tooltip>
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<DismissRegular />}
                    aria-label="关闭"
                    onClick={() => { setDetail(null); setDetailLoading(false); setStarred(false); setShowMeta(true); setComposeOpen(false) }}
                  />
                </div>
              </div>
            </DialogTitle>
            <DialogContent>
              {detailLoading && (
                <div style={{ padding: '40px', textAlign: 'center' }}>
                  <Spinner label="加载邮件内容…" />
                </div>
              )}
              {detail && (
                <>
                  {/* 顶部发件人卡片 */}
                  <div className={styles.mailHero}>
                    <div className={styles.avatarBox}>
                      <Avatar name={fromName(detail.from)} size={40} color="brand" />
                    </div>
                    <div className={styles.heroInfo}>
                      <div className={styles.heroTopRow}>
                        <Text className={styles.heroFrom}>{fromName(detail.from)}</Text>
                        <button
                          type="button"
                          className={styles.heroHideToggle}
                          onClick={() => setShowMeta((v) => !v)}
                        >
                          {showMeta ? '隐藏信息' : '显示信息'}
                          {showMeta ? (
                            <ChevronUp24Regular style={{ verticalAlign: 'middle', marginLeft: 2 }} />
                          ) : (
                            <ChevronDown24Regular style={{ verticalAlign: 'middle', marginLeft: 2 }} />
                          )}
                        </button>
                      </div>
                      <div className={styles.heroEmail}>
                        <span className={styles.heroEmailStrong}>发件人</span>
                        {detail.from ?? '（未知）'}
                      </div>
                      {showMeta && (
                        <>
                          <div className={styles.heroEmail}>
                            <span className={styles.heroEmailStrong}>收件人</span>
                            {detail.recipient}
                          </div>
                          <div className={styles.heroEmail}>
                            <span className={styles.heroEmailStrong}>大小</span>
                            {formatBytes(detail.size)}
                            <span className={styles.metaLabel}>　|　</span>
                            <span className={styles.heroEmailStrong}>时间</span>
                            {formatDate(detail.created_at)}
                          </div>
                          {detail.message_id && (
                            <div className={styles.heroEmail}>
                              <span className={styles.heroEmailStrong}>Message-ID</span>
                              {detail.message_id}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  <div className={styles.divider} />

                  {/* 附件（id=mail-att-anchor，便于顶部"附件"按钮滚动定位） */}
                  {detail.attachments.length > 0 && (
                    <div id="mail-att-anchor" className={styles.attSection} style={{ marginBottom: 16 }}>
                      {detail.attachments.map((att, i) => (
                        <div key={i} className={styles.attItem}>
                          <Attach24Regular />
                          <span className={styles.attName}>{att.filename}（{formatBytes(att.size)}）</span>
                          <Button appearance="subtle" size="small" icon={<ArrowDownloadRegular />} onClick={() => downloadAttachment(att)}>
                            下载
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* 邮件正文 */}
                  <div className={styles.bodyContainer}>
                    {detail.body_html && (
                      <iframe
                        className={styles.bodyFrame}
                        title="HTML 正文"
                        srcDoc={detail.body_html}
                        sandbox=""
                      />
                    )}
                    {detail.body_text && detail.body_html && (
                      <details>
                        <summary style={{ cursor: 'pointer', color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 }}>
                          查看纯文本版本
                        </summary>
                        <div className={styles.bodyText}>{detail.body_text}</div>
                      </details>
                    )}
                    {detail.body_text && !detail.body_html && (
                      <div className={styles.bodyText}>{detail.body_text}</div>
                    )}
                    {!detail.body_html && !detail.body_text && (
                      <div className={styles.bodyText}>（无正文内容）</div>
                    )}
                  </div>

                  {/* 底部快捷操作：回复 / 转发，打开写邮件对话框 */}
                  <div className={styles.replyActions} style={{ marginTop: 18 }}>
                    <Button
                      appearance="subtle"
                      icon={<ArrowForward24Regular />}
                      onClick={() => {
                        setComposeInit({
                          from: detail.recipient,
                          to: detail.from ?? '',
                          subject: detail.subject?.startsWith('Fwd:') ? detail.subject : `Fwd: ${detail.subject ?? ''}`,
                          body: buildForwardBody(detail),
                          title: '转发邮件',
                        })
                        setComposeOpen(true)
                      }}
                    >
                      转发
                    </Button>
                    <Button
                      appearance="primary"
                      icon={<ArrowReplyAll24Regular />}
                      onClick={() => {
                        setComposeInit({
                          from: detail.recipient,
                          to: detail.from ?? '',
                          subject: detail.subject?.startsWith('Re:') ? detail.subject : `Re: ${detail.subject ?? ''}`,
                          body: buildReplyBody(detail),
                          title: '回复邮件',
                        })
                        setComposeOpen(true)
                      }}
                    >
                      回复
                    </Button>
                  </div>
                </>
              )}
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <ComposeModal
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        defaultFrom={composeInit.from}
        defaultTo={composeInit.to}
        defaultSubject={composeInit.subject}
        defaultBody={composeInit.body}
        title={composeInit.title}
      />
    </div>
  )
}